import { SignIn, SignUp } from "@clerk/clerk-react";
import { useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  Check,
  KeyRound,
  MessageSquareText,
  Play,
  Sparkles,
  Workflow,
} from "lucide-react";

type AuthMode = "sign-in" | "sign-up";

const clerkAppearance = {
  elements: {
    rootBox: "landing-clerk-root",
    cardBox: "landing-clerk-card-box",
    card: "landing-clerk-card",
    header: "landing-clerk-header",
    footer: "landing-clerk-footer",
    formButtonPrimary: "landing-clerk-submit",
    formFieldInput: "landing-clerk-input",
    socialButtonsBlockButton: "landing-clerk-social",
    dividerLine: "landing-clerk-divider",
    dividerText: "landing-clerk-divider-text",
  },
  layout: {
    socialButtonsPlacement: "top" as const,
    socialButtonsVariant: "blockButton" as const,
  },
};

const highlights = [
  "Built from agency experience",
  "Strategy-led monthly planning",
  "Client-ready creative reviews",
];

const proofPoints = [
  {
    value: "8+",
    copy: "years of restaurant, retail, and hospitality storytelling shaped into one working method.",
  },
  {
    value: "1",
    copy: "proprietary Ohdelices Creative rhythm for strategy, production, approvals, and publishing.",
  },
  {
    value: "4",
    copy: "connected lanes: brand insight, monthly planning, creative production, and client review.",
  },
  {
    value: "100%",
    copy: "purpose-built around how Ohdelices Creative actually serves social media clients.",
  },
];

const methodCards = [
  {
    label: "Strategy",
    title: "Start with the commercial story.",
    copy: "Translate offers, seasons, launches, habits, and customer behavior into a month with a point of view.",
    items: ["Brand and audience context", "Seasonal opportunity mapping", "Strategic campaign angles"],
    tone: "teal",
    icon: CalendarDays,
  },
  {
    label: "Creative",
    title: "Shape ideas into content clients can feel.",
    copy: "Give every post, story, reel, and campaign beat a reason to exist before it reaches production.",
    items: ["Campaign concept boards", "Caption and visual direction", "Review-ready presentation flow"],
    tone: "tomato",
    icon: MessageSquareText,
  },
  {
    label: "Approval",
    title: "Keep clients close without exposing the backstage.",
    copy: "Invite clear feedback, capture decisions, and move approved work into production with confidence.",
    items: ["Client-friendly review rooms", "Actionable change requests", "Approved production packets"],
    tone: "leaf",
    icon: Workflow,
  },
];

export function UnauthenticatedLandingPage() {
  const [authMode, setAuthMode] = useState<AuthMode>("sign-in");

  return (
    <div className="social-atelier-landing">
      <section className="social-atelier-hero" aria-label="Ohdelices Social Atelier">
        <nav className="social-atelier-nav" aria-label="Landing page navigation">
          <a className="social-atelier-brand" href="#top" aria-label="Ohdelices Social Atelier home">
            <span className="social-atelier-mark">OC</span>
            <span>
              <strong>Ohdelices Social Atelier</strong>
              <small>Designed and built by Ohdelices Creative</small>
            </span>
          </a>
          <div className="social-atelier-nav-links">
            <a href="#method">Method</a>
            <a href="#proof">Results</a>
            <a href="#signin">Sign in</a>
          </div>
        </nav>

        <div id="top" className="social-atelier-hero-grid">
          <div className="social-atelier-copy">
            <p className="social-atelier-eyebrow">An Ohdelices Creative platform</p>
            <h1>Strategic social, shaped like a studio.</h1>
            <p className="social-atelier-intro">
              A custom-built space for turning Ohdelices Creative&apos;s social media strategy, client knowledge,
              campaign planning, and production craft into polished monthly content that is ready to approve.
            </p>
            <div className="social-atelier-actions">
              <a className="social-atelier-enter" href="#signin">
                <ArrowRight size={18} aria-hidden="true" />
                Enter the Atelier
              </a>
              <a className="social-atelier-method-link" href="#method">
                <Play size={18} aria-hidden="true" />
                See the method
              </a>
            </div>
            <div className="social-atelier-highlights" aria-label="Atelier highlights">
              {highlights.map((highlight) => (
                <span key={highlight}>
                  <Check size={16} aria-hidden="true" />
                  {highlight}
                </span>
              ))}
            </div>
          </div>

          <aside id="signin" className="social-atelier-auth" aria-label="Sign in or sign up">
            <header>
              <strong>{authMode === "sign-in" ? "Welcome back" : "Join the Atelier"}</strong>
              <span>
                {authMode === "sign-in"
                  ? "Use your approved Ohdelices Creative account to enter the Atelier."
                  : "Create an account with your approved Ohdelices Creative invitation."}
              </span>
            </header>
            <div className="social-atelier-auth-tabs" role="tablist" aria-label="Authentication mode">
              <button
                type="button"
                role="tab"
                aria-selected={authMode === "sign-in"}
                className={authMode === "sign-in" ? "active" : ""}
                onClick={() => setAuthMode("sign-in")}
              >
                Sign in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={authMode === "sign-up"}
                className={authMode === "sign-up" ? "active" : ""}
                onClick={() => setAuthMode("sign-up")}
              >
                Sign up
              </button>
            </div>
            <div className="social-atelier-auth-body">
              {authMode === "sign-in" ? (
                <SignIn routing="hash" forceRedirectUrl="/" appearance={clerkAppearance} />
              ) : (
                <SignUp routing="hash" forceRedirectUrl="/" appearance={clerkAppearance} />
              )}
              <p className="social-atelier-auth-note">
                <KeyRound size={14} aria-hidden="true" />
                Access is reserved for Ohdelices Creative teams, collaborators, and invited client reviewers.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section id="proof" className="social-atelier-proof" aria-label="Ohdelices Creative experience">
        {proofPoints.map((point) => (
          <article key={point.value}>
            <strong>{point.value}</strong>
            <span>{point.copy}</span>
          </article>
        ))}
      </section>

      <main id="method" className="social-atelier-method">
        <header className="social-atelier-section-head">
          <h2>A branded front door for Ohdelices Creative&apos;s social media method.</h2>
          <p>
            The Atelier is born from client work, sharpened by strategy, and implemented by the same team that
            knows what a strong social calendar has to do.
          </p>
        </header>

        <div className="social-atelier-card-grid">
          {methodCards.map((card) => {
            const Icon = card.icon;
            return (
              <article className="social-atelier-card" key={card.label}>
                <div className="social-atelier-card-top">
                  <span className={`social-atelier-card-icon ${card.tone}`}>
                    <Icon size={22} aria-hidden="true" />
                  </span>
                  <span className="social-atelier-card-label">{card.label}</span>
                </div>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
                <div className="social-atelier-card-list">
                  {card.items.map((item) => (
                    <span key={item}>
                      <Check size={15} aria-hidden="true" />
                      {item}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </main>

      <footer className="social-atelier-footer">
        <Sparkles size={14} aria-hidden="true" />
        Designed and implemented by Ohdelices Creative.
      </footer>
    </div>
  );
}
